import { Box, CircularProgress, Grid, TextField } from "@mui/material";
import { IResourceComponentsProps, useCustom } from "@refinedev/core";
import { useForm } from "@refinedev/react-hook-form";
import React from "react";
import { SaveButton } from "@refinedev/mui";

export const AccountingSettingsForm: React.FC<
  IResourceComponentsProps
> = () => {
  const { data: accountingData, isLoading } = useCustom({
    url: `accounting-settings`,
    method: "get",
    errorNotification: (error) => {
      return {
        message: error?.message as string,
        description: "Error",
        type: "error",
      };
    },
  });

  const {
    refineCore: { formLoading, onFinish },
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      resource: "accounting-settings",
      action: "create",
      successNotification: () => {
        return {
          message: "Accounting settings was updated successfully",
          type: "success",
        };
      },
      errorNotification: (error): any => {
        return {
          message: error?.message,
          type: "error",
        };
      },
    },
    warnWhenUnsavedChanges: true,
  });

  return (
    <Grid>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid style={{ backgroundColor: "white", padding: 10 }}>
          {formLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              <TextField
                {...register("generalJournalCode", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.generalJournalCode}
                helperText={(errors as any)?.generalJournalCode?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.generalJournalCode}
                type="text"
                name="generalJournalCode"
                label="General Journal Code"
              />
              <TextField
                {...register("investmentAccountNumber", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.investmentAccountNumber}
                helperText={(errors as any)?.investmentAccountNumber?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.investmentAccountNumber}
                type="text"
                name="investmentAccountNumber"
                label="Investment Account Number"
              />
              <TextField
                {...register("grantExpenseAccountNumber", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.grantExpenseAccountNumber}
                helperText={(errors as any)?.grantExpenseAccountNumber?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.grantExpenseAccountNumber}
                type="text"
                name="grantExpenseAccountNumber"
                label="Grant Expense Account Number"
              />
              <TextField
                {...register("operatingBankAccountNumber", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.operatingBankAccountNumber}
                helperText={
                  (errors as any)?.operatingBankAccountNumber?.message
                }
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={
                  accountingData?.data.operatingBankAccountNumber
                }
                type="text"
                name="operatingBankAccountNumber"
                label="Operating Bank Account Number"
              />
              <TextField
                {...register("grantProgramId", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.grantProgramId}
                helperText={(errors as any)?.grantProgramId?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.grantProgramId}
                type="text"
                name="grantProgramId"
                label="Grant Program ID"
              />
              <TextField
                {...register("grantClassId", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.grantClassId}
                helperText={(errors as any)?.grantClassId?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.grantClassId}
                type="text"
                name="grantClassId"
                label="Grant Class ID"
              />
              <TextField
                {...register("grantProjectId", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.grantProjectId}
                helperText={(errors as any)?.grantProjectId?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.grantProjectId}
                type="text"
                name="grantProjectId"
                label="Grant Project ID"
              />

              <TextField
                {...register("grantReceiptFolderName")}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.grantReceiptFolderName ?? "Attachments"}
                type="text"
                name="grantReceiptFolderName"
                label="Grant Receipt Folder Name"
              />
              <TextField
                {...register("dafFundReferencePrefix", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.dafFundReferencePrefix}
                helperText={(errors as any)?.dafFundReferencePrefix?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.dafFundReferencePrefix ?? "1"}
                type="text"
                name="dafFundReferencePrefix"
                label="DAF Fund Reference Number prefix"
              />
              <TextField
                {...register("designatedFundReferencePrefix", {
                  required: "This field is required",
                })}
                error={!!(errors as any)?.designatedFundReferencePrefix}
                helperText={(errors as any)?.designatedFundReferencePrefix?.message}
                margin="normal"
                fullWidth
                style={{ marginTop: 10 }}
                InputLabelProps={{ shrink: true }}
                defaultValue={accountingData?.data.designatedFundReferencePrefix ?? "2"}
                type="text"
                name="designatedFundReferencePrefix"
                label="Designated Fund Reference Number prefix"
              />

              <SaveButton
                loading={formLoading}
                onClick={(e: React.BaseSyntheticEvent) => {
                  handleSubmit(onFinish, () => false)(e);
                }}
              >
                Save
              </SaveButton>
            </>
          )}
        </Grid>
      )}
    </Grid>
  );
};
